import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, HelpCircle, Lightbulb, Settings2, Target, Users } from "lucide-react";

import { itemQuery } from "@/lib/queries";
import { DynamicIcon, ShareButtons } from "@/components/site/bits";

export const Route = createFileRoute("/propostas/$slug")({
  head: ({ loaderData }) => {
    const d = loaderData as any;
    if (!d) {
      return {
        meta: [
          { title: "Proposta não encontrada | Professor Rurdiney — 40123" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = d.seo_title || `${d.title} | Professor Rurdiney — 40123`;
    const description = d.seo_description || d.summary || "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
      ],
    };
  },
  loader: async ({ context, params }) => {
    const item = await context.queryClient.ensureQueryData(itemQuery("proposals", params.slug));
    if (!item) throw notFound();
    return item;
  },
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center gap-4 px-4 py-32 text-center">
      <h1 className="text-3xl text-balance-title">Proposta não encontrada</h1>
      <p className="text-muted-foreground">Essa proposta não existe ou foi removida.</p>
      <Link
        to="/propostas"
        className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground"
      >
        <ArrowLeft className="size-4" /> Voltar às propostas
      </Link>
    </div>
  ),
  component: PropostaDetail,
});

const SECTIONS: Array<{ key: string; label: string; icon: any }> = [
  { key: "problem", label: "Problema", icon: HelpCircle },
  { key: "solution", label: "Solução", icon: Lightbulb },
  { key: "how_it_works", label: "Como funciona", icon: Settings2 },
  { key: "expected_impact", label: "Impacto esperado", icon: Target },
  { key: "audience", label: "Público", icon: Users },
];

function PropostaDetail() {
  const { slug } = Route.useParams();
  const { data: proposal } = useSuspenseQuery(itemQuery("proposals", slug));
  if (!proposal) return null;

  return (
    <>
      <header className="relative overflow-hidden bg-hero-gradient px-4 pt-28 pb-16 text-primary-foreground md:pt-36 md:pb-20">
        <div className="absolute inset-0 bg-mesh opacity-40" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-4xl">
          <Link
            to="/propostas"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground/80 hover:text-primary-foreground"
          >
            <ArrowLeft className="size-4" /> Todas as propostas
          </Link>
          <div className="mt-6 flex items-center gap-4">
            <span className="flex size-14 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <DynamicIcon name={proposal.icon} className="size-7" />
            </span>
            <h1 className="text-3xl md:text-5xl text-balance-title">{proposal.title}</h1>
          </div>
          {proposal.summary && (
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/85 md:text-lg">
              {proposal.summary}
            </p>
          )}
        </div>
      </header>

      <section className="px-4 py-16 md:py-20">
        <div className="mx-auto w-full max-w-4xl space-y-6">
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const value = (proposal as any)[key];
            if (!value) return null;
            return (
              <div key={key} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="flex items-center gap-2">
                  <Icon className="size-5 text-accent-deep" />
                  <h2 className="font-display text-xl text-foreground">{label}</h2>
                </div>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">{value}</p>
              </div>
            );
          })}

          <div className="pt-4">
            <ShareButtons title={proposal.title} />
          </div>
        </div>
      </section>
    </>
  );
}
